/**
 * Marketing Onboarding Guard
 *
 * Redirects users to onboarding if they haven't completed it yet.
 * Wraps marketing pages to enforce the onboarding flow.
 */

import { useEffect, useState, ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@trafiflow/shared-ui'
import apiClient from '@/shared/lib/api'
import { ROUTES } from '@/shared/config'

interface OnboardingGuardProps {
  children: ReactNode
}

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [checking, setChecking] = useState(true)
  const [shouldRender, setShouldRender] = useState(false)

  useEffect(() => {
    async function checkOnboardingStatus() {
      if (authLoading || !isAuthenticated) {
        setChecking(false)
        setShouldRender(true)
        return
      }

      try {
        const response = await apiClient.get('/marketing/onboarding/status')
        const data = response.data

        if (data.status === 'completed') {
          // Onboarding complete, allow access
          setShouldRender(true)
        } else {
          // Not completed, redirect to onboarding
          navigate(ROUTES.MARKETING.ONBOARDING, { replace: true })
        }
      } catch {
        // If endpoint doesn't exist, assume onboarding needed for new experience
        // But allow access to avoid breaking existing users
        console.warn('[OnboardingGuard] Status check failed, allowing access')
        setShouldRender(true)
      } finally {
        setChecking(false)
      }
    }

    // Don't check if already on onboarding page
    if (location.pathname === ROUTES.MARKETING.ONBOARDING) {
      setChecking(false)
      setShouldRender(true)
      return
    }

    checkOnboardingStatus()
  }, [authLoading, isAuthenticated, navigate, location.pathname])

  if (checking) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-tf-border border-t-primary rounded-full animate-spin" />
          <p className="text-sm text-tf-text-muted">Loading...</p>
        </div>
      </div>
    )
  }

  if (!shouldRender) {
    return null
  }

  return <>{children}</>
}

export default OnboardingGuard
